import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Skeleton } from 'antd';
import dayjs from 'dayjs';
import { TrackerCustomEvent } from 'common-package';
import { Button } from 'antd-mobile';
import guideImg from '@/assets/image/guide.png';
import releaseNoteImg from '@/assets/image/release-note.png';
import commonIssueImg from '@/assets/image/common-issue.png';
import feebackProgressImg from '@/assets/image/feeback-progress.png';
import skillVideoImg from '@/assets/image/skill-video.png';
import useInstructionImg from '@/assets/image/use-instruction.png';
import emptyBg from '@/assets/image/not-enough-time-bg.png';
import { Header } from '@/components';
import service from '@/service';
import {
  ALL_PAGES,
  BODY_PART,
  COMMON_PROBLEMS_LINK,
  DAYJS_FORMAT,
  MassageType2Text,
  USER_GUIDE_LINK,
  VERSION_INSTRUC_LINK,
} from '@/constant';
import { useNav, useTracker } from '@/hooks';
import { STATES_FILTER } from '../issue-report/select-order';
import QuickGuideModal, { QuickGuideModalRefAttrs } from './QuickGuideModal';
import cx from './index.module.scss';

interface Props {}

export const showTimeText = (timeStamp: number) => {
  if (!timeStamp) return '';
  const isToday = dayjs().format(DAYJS_FORMAT.DATE) === dayjs(timeStamp).format(DAYJS_FORMAT.DATE);
  return dayjs(timeStamp).format(isToday ? 'YYYY-MM-DD（今天） HH:mm' : DAYJS_FORMAT.MINUTES);
};

const orderTimeText = (recentAbnormalOrder: GetLastAbnormalOrderResponse) => {
  if (recentAbnormalOrder?.state === 'Abnormal') {
    return `异常时间：${showTimeText(recentAbnormalOrder?.abnormalTime)}`;
  } else if (recentAbnormalOrder?.state === 'Serving') {
    return `下单时间：${showTimeText(recentAbnormalOrder?.createTime)}`;
  }
};

enum OrderTitle {
  Abnormal = '最近异常订单',
  Serving = '当前设备服务中订单',
}

const HelpFeedback: React.FC<Props> = () => {
  const { sendEvent } = useTracker();
  const [recentAbnormalOrder, setRecentAbnormalOrder] = useState<GetLastAbnormalOrderResponse>(null);
  const [isGettingOrder, setIsGettingOrder] = useState(true);
  const modalRef = useRef<QuickGuideModalRefAttrs>(null);
  const nav = useNav();

  const CARD_ITEMS = useMemo(
    () => [
      {
        iconSrc: feebackProgressImg,
        text: '反馈进度',
        subText: '上报问题的处理进度',
        onClick: () => {
          sendEvent(TrackerCustomEvent.IssueProgressClk, { et: 'CLK' });
          nav(ALL_PAGES.FEEDBACK_PROGRESS);
        },
      },
      {
        iconSrc: commonIssueImg,
        text: '常见问题',
        subText: '使用中的常见疑问&解答',
        onClick: () => {
          sendEvent(TrackerCustomEvent.CommonProblemClk, { et: 'CLK' });
          window.open(COMMON_PROBLEMS_LINK);
        },
      },
      {
        iconSrc: releaseNoteImg,
        text: '版本说明',
        subText: '软件版本升级说明',
        onClick: () => {
          sendEvent(TrackerCustomEvent.ReleaseNoteClk, { et: 'CLK' });
          window.open(VERSION_INSTRUC_LINK);
        },
      },
      {
        iconSrc: guideImg,
        text: '快速指南',
        subText: '机器人简易操作指南',
        onClick: () => {
          sendEvent(TrackerCustomEvent.QuickGuideClk, { et: 'CLK' });
          modalRef.current.show();
        },
      },
      {
        iconSrc: skillVideoImg,
        text: '使用技巧（视频）',
        subText: '机器人使用演示视频',
        onClick: () => {
          sendEvent(TrackerCustomEvent.UsingSkillClk, { et: 'CLK' });
          nav(ALL_PAGES.USE_SKILLS);
        },
      },
      {
        iconSrc: useInstructionImg,
        text: '使用说明书',
        subText: '详细的机器人使用说明',
        onClick: () => {
          sendEvent(TrackerCustomEvent.UseDescClk, { et: 'CLK' });
          window.open(USER_GUIDE_LINK);
        },
      },
    ],
    [nav, sendEvent]
  );

  const initData = useCallback(async () => {
    try {
      const { data } = await service.service.getLastAbnormalOrder();
      setRecentAbnormalOrder(data);
    } finally {
      setIsGettingOrder(false);
    }
  }, []);

  useEffect(() => {
    initData();
  }, [initData]);

  return (
    <div className={cx('HelpFeedback')}>
      <Header backConfig={{ text: '信息', action: () => nav('/pad-info') }} title="帮助与反馈" />

      <div className={cx('card-box')}>
        {CARD_ITEMS.map(item => (
          <div className={cx('card-item')} key={item.text} onClick={() => item?.onClick?.()}>
            <div className={cx('card-img')}>
              <img src={item.iconSrc} alt="" />
            </div>
            <div className={cx('main-text')}>{item.text}</div>
            <div className={cx('sub-text')}>{item.subText}</div>
          </div>
        ))}
      </div>

      <div className={cx('issue-box')}>
        {!!recentAbnormalOrder?.id && (
          <div className={cx('recent-abnormal-order')}>
            <div className={cx('header')}>{OrderTitle[recentAbnormalOrder?.state]}</div>

            <div className={cx('content')}>
              <div className={cx('order-info')}>
                <div className={cx('plan-name')}>{recentAbnormalOrder?.goodsName}</div>
                <div className={cx('order-state', recentAbnormalOrder?.state)}>
                  {STATES_FILTER.find(item2 => item2.value === recentAbnormalOrder?.state)?.stateText}
                </div>
              </div>
              <div className={cx('plan-info')}>
                {BODY_PART[recentAbnormalOrder?.bodyPart] ? `${BODY_PART[recentAbnormalOrder?.bodyPart]} | ` : ''}
                {MassageType2Text[recentAbnormalOrder?.itemType]}
              </div>
              <div className={cx('abnormal-time')}>{orderTimeText(recentAbnormalOrder)}</div>
            </div>
            <div className={cx('footer')}>
              <Button
                block
                className={cx('btn')}
                onClick={() => {
                  sendEvent(TrackerCustomEvent.ReportIssueClk, { et: 'CLK' });
                  nav(ALL_PAGES.ISSUE_FEEDBACK, {
                    state: {
                      id: recentAbnormalOrder?.id,
                    },
                  });
                }}>
                立即上报
              </Button>
            </div>
          </div>
        )}

        <div className={cx('to-feedback')}>
          <div className={cx('tip-text')}>如有历史订单问题也可以进行问题反馈</div>
          <Button
            className={cx('btn')}
            onClick={() => {
              sendEvent(TrackerCustomEvent.IssueFeedbackClk, { et: 'CLK' });
              nav(ALL_PAGES.ISSUE_FEEDBACK);
            }}>
            问题反馈
          </Button>
        </div>
      </div>

      <QuickGuideModal ref={modalRef} />
    </div>
  );
};

export default HelpFeedback;
